import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { billingApi } from '../../api/billing';

interface Plan {
  id: number;
  name: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
}

interface Subscription {
  status: string;
  plan_name: string;
  plan_id?: number;
  features: string[];
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  expires_at?: string;
}

export const PlanSelection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [plansResponse, subscriptionResponse] = await Promise.all([
        billingApi.getPlans(),
        billingApi.getUserSubscription()
      ]);
      
      setPlans(plansResponse.plans);
      setCurrentSubscription(subscriptionResponse);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId: number) => {
    if (checkoutLoading) return;
    
    setCheckoutLoading(planId);
    try {
      const response = await billingApi.createCheckoutSession(planId);
      window.location.href = response.url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert(t('plan.paymentFailed'));
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm(t('billing.cancelSubscriptionConfirm'))) {
      return;
    }

    try {
      await billingApi.cancelSubscription();
      alert(t('billing.cancelSubscriptionSuccess'));
      loadData();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert(t('billing.cancelSubscriptionFailed'));
    }
  };

  const formatPrice = (amount: number) => {
    return `¥${amount.toLocaleString()}`;
  };

  const getFeatureLabel = (feature: string) => {
    const translationKey = `billing.feature.${feature}`;
    return t(translationKey, { defaultValue: feature });
  };

  const getPlanName = (planName: string) => {
    // Map API plan names to translation keys
    const planNameMap: { [key: string]: string } = {
      'フリープラン': 'plan.free',
      'Free': 'plan.free',
      'FREE': 'plan.free',
      'プロフェッショナル': 'plan.professional',
      'Professional': 'plan.professional',
      'PRO': 'plan.professional',
      'BUSINESS': 'plan.business',
      'SCHOOL': 'plan.school'
    };
    
    const translationKey = planNameMap[planName];
    return translationKey ? t(translationKey) : planName;
  };

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="plan-selection">
      {currentSubscription && (
        <div className="current-subscription">
          <h3>{t('billing.currentPlan')}</h3>
          <div className="subscription-card">
            <div className="plan-name">{currentSubscription.plan_name}</div>
            <div className="plan-status">
              {currentSubscription.status === 'developer_bypass' ? t('billing.status.developerAccess') : 
                           currentSubscription.status === 'active' ? t('billing.status.active') :
                           currentSubscription.status === 'free' ? t('billing.status.free') : currentSubscription.status}
            </div>
            
            {currentSubscription.current_period_end && (
              <div className="period-end">
                <strong>{t('billing.nextRenewalDate')}:</strong> {new Date(currentSubscription.current_period_end).toLocaleDateString(i18n.language)}
                {currentSubscription.status === 'active' && !currentSubscription.cancel_at_period_end && (
                  <span className="auto-renewal-note"> {t('billing.autoRenewalNote')}</span>
                )}
              </div>
            )}
            
            {currentSubscription.expires_at && (
              <div className="expires-at">
                {t('billing.expiresAt')}: {new Date(currentSubscription.expires_at).toLocaleDateString(i18n.language)}
              </div>
            )}
            
            {currentSubscription.cancel_at_period_end && (
              <div className="cancel-notice">
                {t('billing.cancelNotice')}
              </div>
            )}
            
            {currentSubscription.status === 'active' && !currentSubscription.cancel_at_period_end && (
              <button 
                onClick={handleCancelSubscription}
                className="cancel-button"
              >
                {t('billing.cancelSubscription')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="plans-grid">
        {plans.map((plan) => (
          <div key={plan.id} className="plan-card">
            <div className="plan-header">
              <h3>{getPlanName(plan.name)}</h3>
              <span className="plan-subtitle">{t('billing.forIndividual')}</span>
              <div className="plan-price">
                {plan.amount === 0 ? t('billing.free') : formatPrice(plan.amount)}
                {plan.amount > 0 && <span className="plan-interval">/{plan.interval === 'month' ? t('billing.month') : t('billing.year')}</span>}
              </div>
              {plan.amount > 0 && (
                <div className="plan-billing-info">
                  {t('plan.monthlyRenewal')} • {t('plan.cancelAnytime')}
                </div>
              )}
            </div>
            
            <div className="plan-features">
              <h4>{t('billing.features')}:</h4>
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}>{getFeatureLabel(feature)}</li>
                ))}
              </ul>
              {plan.amount > 0 && (
                <p className="ai-usage-limitation">
                  {t('billing.aiUsageLimitation')}
                </p>
              )}
            </div>
            
            <div className="plan-actions">
              {(() => {
                // 現在のプランかどうかの判定
                console.log(`[Plan Debug] Plan: ${plan.name} (ID: ${plan.id}), Amount: ${plan.amount}`);
                console.log(`[Plan Debug] Current Subscription:`, {
                  status: currentSubscription?.status,
                  plan_name: currentSubscription?.plan_name,
                  plan_id: currentSubscription?.plan_id
                });
                
                const isCurrentPlan = (() => {
                  if (!currentSubscription) {
                    // サブスクリプション情報がない場合、フリープランが現在のプラン
                    return plan.amount === 0;
                  }
                  
                  // プランIDがある場合はそれで判定、ない場合はプラン名で判定
                  if (currentSubscription.plan_id !== undefined) {
                    return currentSubscription.plan_id === plan.id;
                  }
                  
                  // フリープランの判定
                  if (plan.amount === 0) {
                    return currentSubscription.status === 'free' || 
                           currentSubscription.status === 'inactive' || 
                           !currentSubscription.status ||
                           currentSubscription.plan_name === 'フリープラン' ||
                           currentSubscription.plan_name === 'FREE';
                  }
                  
                  // 開発者バイパスの場合は、PRO相当として扱う
                  if (currentSubscription.status === 'developer_bypass') {
                    return plan.name.toLowerCase().includes('pro') || 
                           plan.name.toLowerCase().includes('premium') ||
                           (plan.amount > 0 && plan.amount < 5000); // PRO程度の価格帯
                  }
                  
                  // 有料プランの判定
                  return currentSubscription.status === 'active' && 
                         (currentSubscription.plan_name === plan.name ||
                          currentSubscription.plan_name.toLowerCase() === plan.name.toLowerCase());
                })();
                
                console.log(`[Plan Debug] Plan ${plan.name} isCurrentPlan: ${isCurrentPlan}`, {
                  planAmount: plan.amount,
                  subscriptionStatus: currentSubscription?.status,
                  subscriptionPlanName: currentSubscription?.plan_name
                });

                if (isCurrentPlan) {
                  return plan.amount === 0 ? (
                    <div className="free-plan-note">{t('billing.currentPlan')}</div>
                  ) : (
                    <button disabled className="plan-button current">
                      {t('billing.currentPlan')}
                    </button>
                  );
                } else if (plan.amount > 0) {
                  // 無料プランでない場合のみアップグレードボタンを表示
                  return (
                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={checkoutLoading === plan.id}
                      className="plan-button upgrade"
                    >
                      {checkoutLoading === plan.id ? t('common.processing') : t('billing.upgrade')}
                    </button>
                  );
                } else {
                  // 無料プランで現在のプランでない場合は何も表示しない（ダウングレードは不要）
                  return null;
                }
              })()}
            </div>
          </div>
        ))}
        
        {/* エンタープライズプラン - 企業向け */}
        <div className="plan-card enterprise-plan">
          <div className="plan-header">
            <h3>BUSINESS<span className="plan-subtitle">{t('billing.enterprise')}</span></h3>
            <div className="plan-price">
              {t('billing.businessPrice')}<span className="plan-interval">{t('billing.pricePerMonth')}</span>
            </div>
            <div className="plan-billing-info">
              {t('billing.customQuote')} • {t('billing.flexibleCustomization')}
            </div>
          </div>
          
          <div className="plan-features">
            <h4>{t('billing.features')}:</h4>
            <ul>
              <li>{t('billing.feature.allProfessionalFeatures')}</li>
              <li>{t('billing.feature.unlimitedUsers')}</li>
              <li>{t('billing.feature.dedicatedSupportAgent')}</li>
              <li>{t('billing.feature.customIntegrationAPI')}</li>
              <li>{t('billing.feature.customBranding')}</li>
              <li>{t('billing.feature.singleSignOn')}</li>
              <li>{t('billing.feature.auditLogSecurity')}</li>
              <li>{t('billing.feature.onPremiseDeployment')}</li>
              <li>{t('billing.feature.trainingImplementationSupport')}</li>
            </ul>
          </div>
          
          <div className="plan-actions">
            <button
              onClick={() => window.open('/enterprise-quote/corporate', '_blank')}
              className="plan-button enterprise"
            >
              {t('billing.requestCorporateQuote')}
            </button>
          </div>
        </div>

        {/* エンタープライズプラン - 学校向け */}
        <div className="plan-card enterprise-plan academic">
          <div className="plan-header">
            <h3>SCHOOL<span className="plan-subtitle">{t('billing.education')}</span></h3>
            <div className="plan-price">
              {t('billing.schoolPrice')}<span className="plan-interval">{t('billing.pricePerMonth')}</span>
            </div>
            <div className="plan-billing-info">
              {t('billing.educationalDiscount')} • {t('billing.customQuote')}
            </div>
          </div>
          
          <div className="plan-features">
            <h4>{t('billing.features')}:</h4>
            <ul>
              <li>{t('billing.feature.allProfessionalFeatures')}</li>
              <li>{t('billing.feature.unlimitedStudentFacultyAccounts')}</li>
              <li>{t('billing.feature.educationalInstitutionSpecialPricing')}</li>
              <li>{t('billing.feature.classroomPracticalTemplates')}</li>
              <li>{t('billing.feature.gradeManagementSystemIntegration')}</li>
              <li>{t('billing.feature.learningAnalyticsReporting')}</li>
              <li>{t('billing.feature.parentGuardianSharingFeature')}</li>
              <li>{t('billing.feature.educationalInstitutionDedicatedSupport')}</li>
              <li>{t('billing.feature.implementationTrainingTeacherTraining')}</li>
            </ul>
          </div>
          
          <div className="plan-actions">
            <button
              onClick={() => window.open('/enterprise-quote/academic', '_blank')}
              className="plan-button enterprise academic"
            >
              {t('billing.requestAcademicQuote')}
            </button>
          </div>
        </div>
      </div>
      
      <div className="billing-info">
        <h3>{t('billing.aboutBilling')}</h3>
        <ul>
          <li><strong>{t('billing.autoRenewal')}：</strong>{t('billing.autoRenewalDescription')}</li>
          <li><strong>{t('billing.nextRenewalDate')}：</strong>{t('billing.nextRenewalDateDescription')}</li>
          <li><strong>{t('billing.cancellation')}：</strong>{t('billing.cancellationDescription')}</li>
          <li><strong>{t('billing.payment')}：</strong>{t('billing.paymentDescription')}</li>
          <li><strong>{t('billing.receipt')}：</strong>{t('billing.receiptDescription')}</li>
        </ul>
      </div>
    </div>
  );
};